"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useSelector } from "react-redux";

const WATI_SCRIPT_SRC =
    "https://wati-integration-service.clare.ai/ShopifyWidget/shopifyWidget.js?89354";

const EXCLUDED_ROUTES = [
    "/login",
    "/password-reset-request",
    "/reset-request-verify",
    "/password-reset",
    "/authorization",
    "/register",
    "/maintenance-mode",
];

function isExcludedRoute(pathname = "") {
    return EXCLUDED_ROUTES.some((route) => pathname.startsWith(route));
}

function removeWhatsappWidget() {
    const widgetSelectors = [
        "#whatsapp_chat_widget",
        "#wa-widget",
        "#wa-chat-widget",
        ".wa-widget",
        ".wa-chat-box",
        ".wa-chat-bubble",
    ];

    widgetSelectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
            element.remove();
        });
    });

    document
        .querySelectorAll('script[src*="clare.ai"]')
        .forEach((script) => script.remove());
}

export default function WhatsAppWidget() {
    const pathname = usePathname();
    const { data, status } = useSelector((state) => state?.extension?.whatsapp);
    const initializedPhoneRef = useRef(null);

    const shortcode = data?.data?.extension?.shortcode;
    const phoneNumber = shortcode?.phoneNumber?.value || "";
    const isExcluded = isExcludedRoute(pathname);

    useEffect(() => {
        return () => {
            removeWhatsappWidget();
            initializedPhoneRef.current = null;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        if (isExcluded) {
            removeWhatsappWidget();
            initializedPhoneRef.current = null;
            return undefined;
        }

        if (status !== "succeeded" || !shortcode || !phoneNumber) {
            return undefined;
        }

        if (initializedPhoneRef.current === phoneNumber) {
            return undefined;
        }

        removeWhatsappWidget();

        const script = document.createElement("script");
        script.src = WATI_SCRIPT_SRC;
        script.async = true;
        script.dataset.whatsappWidget = "true";

        script.onload = () => {
            if (
                cancelled ||
                isExcludedRoute(pathname) ||
                typeof CreateWhatsappChatWidget !== "function"
            ) {
                return;
            }

            removeWhatsappWidget();

            CreateWhatsappChatWidget({
                enabled: true,
                chatButtonSetting: {
                    backgroundColor: "#4dc247",
                    ctaText: shortcode?.buttonCtaText?.value || "WhatsApp",
                    borderRadius: "25",
                    marginLeft: shortcode?.buttonMarginLeft?.value || "0",
                    marginBottom: shortcode?.buttonMarginBottom?.value || "80",
                    marginRight: shortcode?.buttonMarginRight?.value || "50",
                    position: shortcode?.buttonPosition?.value || "left",
                },
                brandSetting: {
                    brandName: shortcode?.brandName?.value || "Brand",
                    brandSubTitle: shortcode?.brandSubTitle?.value || "",
                    brandImg: "/favicon.png",
                    welcomeText:
                        shortcode?.welcomeText?.value ||
                        "Hi there! How can I help you?",
                    backgroundColor: "#0a5f54",
                    ctaText: shortcode?.ctaText?.value || "Start Chat",
                    borderRadius: "25",
                    autoShow: false,
                    phoneNumber,
                },
            });

            initializedPhoneRef.current = phoneNumber;
        };

        document.body.appendChild(script);

        return () => {
            cancelled = true;
            script.remove();
        };
    }, [isExcluded, status, phoneNumber, pathname]);

    return null;
}
